import {
  adminClient,
  deviceAuthorizationClient,
  magicLinkClient,
  organizationClient,
} from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

/**
 * better-auth client factory — the auth twin of the Eden factory in
 * `index.ts`. Sign-in/up, magic links, org creation, invitations and member
 * management all speak to `/api/auth/*` through this client; the Eden client
 * covers our own `/api/v1/*` domain routes.
 *
 * Platforms differ only in transport details:
 * - web: cookies ride along (`credentials: 'include'` for the cross-origin
 *   dev servers).
 * - mobile: builds its own client with `@better-auth/expo`'s plugin appended
 *   (SecureStore-backed cookie jar); see apps/mobile.
 */

/** The plugin set every platform shares. Spread first, append platform extras. */
export const sharedAuthPlugins = () =>
  [
    // An invitation may name the directory person it is for (`personId`,
    // "Invite to sign in"); the server's organization plugin declares it too.
    organizationClient({
      schema: {
        invitation: { additionalFields: { personId: { type: 'string', required: false } } },
      },
    }),
    magicLinkClient(),
    adminClient(),
    deviceAuthorizationClient(),
  ] as const

export function createWebAuthClient(baseUrl: string) {
  return createAuthClient({
    baseURL: baseUrl,
    basePath: '/api/auth',
    plugins: [...sharedAuthPlugins()],
    fetchOptions: { credentials: 'include' },
  })
}

/**
 * The client surface shared code may rely on. Both the web client above and
 * mobile's expo-flavored client structurally satisfy this type — the expo
 * plugin only changes transport, not the API.
 */
export type AuthClient = ReturnType<typeof createWebAuthClient>
